import { InboxConversation } from '@spike/omnichannel-model';
import { useAuth } from 'hooks';
import Spinner from 'components/UI/V3/Spinner';
import moment from 'moment-timezone';
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import MessageBubble from './MessageBubble';
import styles from './MessageThread.module.scss';

/** How close to the top counts as "the user wants what came before". */
const LOAD_OLDER_THRESHOLD_PX = 120;

interface MessageThreadProps {
    conversation: InboxConversation;
    onAttachmentsExpired: () => void;
    onLoadOlder?: () => void;
    loadingOlder?: boolean;
    hasOlder?: boolean;
}

const dayLabelOf = (isoDate: string, timeZone: string): string => {
    const date = moment(isoDate).tz(timeZone);
    const now = moment().tz(timeZone);
    if (date.isSame(now, 'day')) return 'Today';
    if (date.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
    return date.format('dddd, MMM D');
};

export const MessageThread: React.FC<MessageThreadProps> = ({
    conversation,
    onAttachmentsExpired,
    onLoadOlder,
    loadingOlder,
    hasOlder
}) => {
    const auth = useAuth();
    const scrollRef = useRef<HTMLDivElement>(null);
    const firstMessageIdRef = useRef<number | undefined>(undefined);
    const scrollHeightRef = useRef(0);

    const groups = useMemo(() => {
        const byDay = new Map<string, typeof conversation.messages>();

        conversation.messages.forEach(message => {
            const label = dayLabelOf(message.sentAt, auth.timeZone!);
            const group = byDay.get(label) || [];
            group.push(message);
            byDay.set(label, group);
        });

        return Array.from(byDay.entries());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation.messages]);

    /**
     * Where the messages were added decides where the view goes. An older page is prepended, so
     * jumping to the bottom would both lose the reading position and bounce the user back to the
     * top boundary, requesting page after page.
     */
    useLayoutEffect(() => {
        const element = scrollRef.current;
        if (!element) return;

        const firstId = conversation.messages[0]?.id;
        const grewAtTop = firstMessageIdRef.current !== undefined && firstId !== firstMessageIdRef.current;
        const previousScrollHeight = scrollHeightRef.current;

        firstMessageIdRef.current = firstId;
        scrollHeightRef.current = element.scrollHeight;

        // The container scrolls smoothly, so an assignment here would ANIMATE from the current
        // position. On the way down it passes the top of the thread and fires scroll events that
        // read as "the user reached the top", loading page after page on its own. Move instantly.
        const previousBehavior = element.style.scrollBehavior;
        element.style.scrollBehavior = 'auto';
        element.scrollTop = grewAtTop ? element.scrollHeight - previousScrollHeight : element.scrollHeight;
        element.style.scrollBehavior = previousBehavior;
    }, [conversation.id, conversation.messages]);

    const handleScroll = useCallback(() => {
        const element = scrollRef.current;
        if (!element || !onLoadOlder || !hasOlder || loadingOlder) return;
        // a thread that does not overflow has no top to reach
        if (element.scrollHeight <= element.clientHeight) return;
        if (element.scrollTop <= LOAD_OLDER_THRESHOLD_PX) onLoadOlder();
    }, [hasOlder, loadingOlder, onLoadOlder]);

    return (
        <div
            ref={scrollRef}
            className={styles.root}
            onScroll={handleScroll}
        >
            {loadingOlder && (
                <div className={styles.loadingOlder}>
                    <Spinner />
                </div>
            )}

            {groups.map(([label, messages]) => (
                <React.Fragment key={label}>
                    <div className={styles.dayDivider}>
                        <span>{label}</span>
                    </div>

                    {messages.map(message => (
                        <MessageBubble
                            key={message.id}
                            message={message}
                            clientName={conversation.contact.name}
                            onAttachmentsExpired={onAttachmentsExpired}
                        />
                    ))}
                </React.Fragment>
            ))}
        </div>
    );
};

export default MessageThread;
